import type { CalendarEvent, ListCalendarEventsResponse } from "@scoach/types";
import { Chev, Globe, Play, Search, Users, Video } from "@scoach/ui/icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuthStore } from "../../auth/store.ts";
import { connectGoogleCalendar } from "../../../lib/firebase.ts";
import { canUseGis, getCalendarAuthCode, preloadGis } from "../../../lib/googleToken.ts";
import { api } from "../../../lib/http.ts";

interface Props {
  onStartCoaching: (event: CalendarEvent) => void;
  /** Run a Discovery on the event's people (external meetings only). */
  onDiscover: (event: CalendarEvent) => void;
}

/** External = at least one real PERSON (not the user, not a room/resource) whose email
 * domain differs from the user's organization. */
function isExternalEvent(event: CalendarEvent, currentUserEmail?: string | null): boolean {
  const myEmail = (currentUserEmail || "").trim().toLowerCase();
  const myDomain = myEmail.includes("@") ? (myEmail.split("@")[1] || "") : "";
  const isGeneric = ["gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "yahoo.com", "icloud.com"].includes(myDomain);

  return event.attendees.some((a) => {
    if (a.self || a.resource) return false;
    const aEmail = a.email.toLowerCase();
    if (aEmail === myEmail) return false;
    if (!myDomain || isGeneric) return true;
    if (myDomain === "google.com") {
      return !aEmail.endsWith("@google.com") && !aEmail.endsWith("@altostrat.com");
    }
    return !aEmail.endsWith(`@${myDomain}`);
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** The other PEOPLE on the invite (everyone but the current user and rooms/resources). */
function otherAttendees(event: CalendarEvent): CalendarEvent["attendees"] {
  return event.attendees.filter((a) => !a.self && !a.resource);
}

// UI hint only (server is the source of truth via the stored refresh token): start
// "connected" if we were last time, so the Connect card doesn't flash on every visit.
const CAL_CONNECTED_KEY = "sally.calConnected";
// Pull the next 3 days first, then let the user load more (3 at a time) up to 10.
const INITIAL_DAYS = 3;
const STEP_DAYS = 3;
const MAX_DAYS = 10;
// No history: the strip starts at today and only shows the coming days.
const PAST_DAYS = 0;

/** Card date label: Today / Tomorrow / Yesterday / "Wed, Sep 3". */
function dateLabel(iso: string): string {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function UpcomingMeetings({ onStartCoaching, onDiscover }: Props) {
  const userEmail = useAuthStore((s) => s.user?.email ?? s.email);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  // Start "connected" if the user has connected before or has a stored Google access token.
  const [connected, setConnected] = useState(
    () => localStorage.getItem(CAL_CONNECTED_KEY) === "1" || Boolean(useAuthStore.getState().googleAccessToken),
  );
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [days, setDays] = useState(INITIAL_DAYS);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);

  // After events load, position the strip so TODAY is at the left edge — the user
  // scrolls left for past days, right for the coming days.
  useEffect(() => {
    const c = scrollRef.current;
    const t = todayRef.current;
    if (c && t) c.scrollLeft = Math.max(0, t.offsetLeft - c.offsetLeft - 4);
  }, [events]);

  function scrollBy(dir: -1 | 1) {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" });
  }

  // Fetch the window. The server mints the Google access token from the user's stored
  // refresh token, so no token juggling here. `connected:false` means "not connected"
  // (no refresh token / revoked) → show the Connect card. Returns "ok" | "disconnected"
  // | "error" so callers can distinguish a real transient failure from needing consent.
  const load = useCallback(async (windowDays: number): Promise<"ok" | "disconnected" | "error"> => {
    try {
      const res = await api<ListCalendarEventsResponse>("/calendar/events", {
        query: { days: windowDays, past: PAST_DAYS },
      });
      if (res.connected === false) {
        setConnected(false);
        localStorage.removeItem(CAL_CONNECTED_KEY);
        useAuthStore.getState().setGoogleAccessToken(null);
        return "disconnected";
      }
      setEvents(res.events ?? []);
      setConnected(true);
      setDays(windowDays);
      setError(null);
      localStorage.setItem(CAL_CONNECTED_KEY, "1");
      return "ok";
    } catch {
      // Transient network / 5xx — do NOT flip to the Connect card (the refresh
      // token is still valid server-side); keep whatever we last showed.
      return "error";
    }
  }, []);

  useEffect(() => {
    preloadGis();
    let cancelled = false;
    (async () => {
      await load(INITIAL_DAYS);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  // One-time consent: try GIS auth-code flow first; if unavailable or unconfigured,
  // fall back seamlessly to Firebase popup with calendar scope.
  async function connect() {
    setConnecting(true);
    setError(null);
    try {
      let codeSuccess = false;
      if (canUseGis()) {
        try {
          const code = await getCalendarAuthCode();
          if (code) {
            await api("/calendar/connect", { method: "POST", body: { code } });
            codeSuccess = true;
          }
        } catch (gisErr) {
          console.warn("GIS calendar connect attempt failed, falling back to Firebase popup:", gisErr);
        }
      }

      if (!codeSuccess) {
        const token = await connectGoogleCalendar();
        useAuthStore.getState().setGoogleAccessToken(token);
      }

      const res = await load(INITIAL_DAYS);
      if (res !== "ok") {
        setError("Connected, but couldn't load your calendar. Try again.");
      }
    } catch (err: any) {
      console.error("Failed to connect Google Calendar:", err);
      const msg = String(err?.message || err || "");
      if (msg.includes("access_denied") || msg.includes("verification") || err?.code === "auth/popup-closed-by-user") {
        setError("במסך האבטחה של Google יש ללחוץ על 'מתקדם' (Advanced) ואז 'כניסה אל SellSynq (לא מאובטח)'. בנוסף ודא שהאפליקציה בסטטוס Publish ב-Google Cloud Console.");
      } else {
        setError("Couldn't connect Google Calendar — allow the popup and try again.");
      }
    } finally {
      setConnecting(false);
    }
  }

  async function loadMore() {
    const next = Math.min(days + STEP_DAYS, MAX_DAYS);
    if ((await load(next)) === "error") {
      setError("Couldn't load more days — try again.");
    }
  }

  // Horizontal card per meeting. Future/now cards let you start (or research) the
  // meeting; past ("older") cards link to the meeting's summary.
  function renderCard(event: CalendarEvent, isAnchor: boolean) {
    const others = otherAttendees(event);
    const external = isExternalEvent(event, userEmail);
    const isNow = new Date(event.start).getTime() - Date.now() < 30 * 60_000;
    const who = others
      .slice(0, 3)
      .map((a) => a.displayName || a.email.split("@")[0])
      .join(", ");
    return (
      <div
        key={event.id}
        ref={isAnchor ? todayRef : undefined}
        className={`upcoming-card${isNow ? " upcoming-soon" : ""}`}
      >
        <div className="upcoming-time-row">
          <span className="upcoming-date">{dateLabel(event.start)}</span>
          <span className="upcoming-clock mono">{formatTime(event.start)}</span>
          <span className={`upcoming-tag ${external ? "ext" : "int"}`}>
            {external ? "External" : "Internal"}
          </span>
        </div>
        <div className="upcoming-title">
          {event.summary}
          {event.meetLink && (
            <a
              href={event.meetLink}
              target="_blank"
              rel="noreferrer"
              className="upcoming-meet-icon"
              title="Join the Google Meet (opens in a new tab)"
              onClick={(e) => e.stopPropagation()}
            >
              <Video size={15} />
            </a>
          )}
        </div>
        {who && (
          <div className="upcoming-attendees">
            <Users size={12} />
            <span>
              {who}
              {others.length > 3 && ` +${others.length - 3}`}
            </span>
          </div>
        )}
        {/* Actions pinned to the card bottom so they line up across cards of any height. */}
        <div className="upcoming-card-actions">
          {external && (
            <button
              type="button"
              className="ghost-btn xs"
              title="Research the people on this invite in Discovery"
              onClick={() => onDiscover(event)}
            >
              <Search size={12} /> Discovery
            </button>
          )}
          <button
            type="button"
            className="pill-btn primary sm"
            onClick={() => onStartCoaching(event)}
          >
            <Play size={12} /> Start meeting
          </button>
        </div>
      </div>
    );
  }

  if (loading) return null;

  if (!connected) {
    return (
      <section className="dash-upcoming">
        <div className="dash-upcoming-head">
          <h2 className="dash-h2">
            <Globe size={16} /> Upcoming meetings
          </h2>
        </div>
        <div className="upcoming-connect">
          <p style={{ color: "var(--text-3)", fontSize: 13, margin: "0 0 12px", lineHeight: 1.5 }}>
            Connect your Google Calendar and SellSynq will show your meetings that include other people
            — so you can jump straight into a meeting. Solo holds are skipped.
          </p>
          <button
            type="button"
            className="pill-btn primary"
            onClick={connect}
            disabled={connecting}
          >
            <Globe size={13} /> {connecting ? "Connecting…" : "Connect Google Calendar"}
          </button>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-3)",
              marginTop: 10,
              lineHeight: 1.5,
              background: "var(--surface-2, rgba(255,255,255,0.04))",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--border-soft, rgba(255,255,255,0.08))",
              maxWidth: 520,
            }}
          >
            <span style={{ fontWeight: 600, color: "var(--text-2)" }}>💡 שימו לב: </span>
            אם מופיע מסך <em>"Google לא אימתה את האפליקציה"</em> — לחצו על <strong>מתקדם (Advanced)</strong> ולאחר מכן על <strong>כניסה אל SellSynq (לא מאובטח)</strong> כדי לאשר את החיבור.
          </div>
          {error && (
            <div style={{ color: "var(--gc-red, #d93025)", fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>
              <div>{error}</div>
              <a
                href="https://console.cloud.google.com/apis/credentials/consent?project=recruitment-502218"
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--brand-primary, #6366f1)", fontWeight: 600, textDecoration: "underline", display: "inline-block", marginTop: 4 }}
              >
                פתח את מסך OAuth ב-Google Cloud Console לאישור ←
              </a>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Chronological strip starting at today: today first, the coming days to the right.
  const sorted = [...events].sort((a, b) => a.start.localeCompare(b.start));
  const anchorIdx = 0;

  return (
    <section className="dash-upcoming">
      <div className="dash-upcoming-head">
        <h2 className="dash-h2">
          <Globe size={16} /> Upcoming meetings
        </h2>
        <div className="upcoming-nav">
          {days < MAX_DAYS && (
            <button type="button" className="ghost-btn xs" onClick={loadMore}>
              Load more days
            </button>
          )}
          <button
            type="button"
            className="icon-btn xs"
            aria-label="Scroll to earlier meetings"
            onClick={() => scrollBy(-1)}
          >
            <Chev size={16} style={{ transform: "rotate(90deg)" }} />
          </button>
          <button
            type="button"
            className="icon-btn xs"
            aria-label="Scroll to later meetings"
            onClick={() => scrollBy(1)}
          >
            <Chev size={16} style={{ transform: "rotate(-90deg)" }} />
          </button>
        </div>
      </div>
      {sorted.length === 0 ? (
        <p style={{ color: "var(--text-4)", fontSize: 13 }}>
          No meetings with other people in the next {days} days.
        </p>
      ) : (
        <div className="upcoming-scroll" ref={scrollRef}>
          {sorted.map((e, i) => renderCard(e, i === anchorIdx))}
        </div>
      )}
      {error && (
        <div style={{ color: "var(--gc-red, #d93025)", fontSize: 12, marginTop: 8 }}>{error}</div>
      )}
    </section>
  );
}
